'use client';

import React, { useState, useEffect, useCallback } from 'react';
import { Toaster, toast } from 'sonner';
import Icon from '@/components/ui/AppIcon';
import { createClient } from '@/lib/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import ConfirmDialog from '@/components/ui/ConfirmDialog';
import Breadcrumb from '@/components/Breadcrumb';

// Create supabase client once outside the component to avoid infinite re-render
const supabase = createClient();

interface Subnet {
  id: string;
  nome: string;
  cidr: string;
  gateway: string | null;
  area: string;
  descrizione: string;
  ip_totali: number;
  ip_usati: number;
  created_at: string;
}

interface Vlan {
  id: string;
  nome: string;
  vlan_id: number;
  area: string;
  descrizione: string;
  subnet_id: string | null;
  created_at: string;
  subnet?: { nome: string; cidr: string } | null;
}

const areaLabels: Record<string, { label: string; color: string }> = {
  laboratorio: { label: 'Laboratorio', color: 'text-emerald-400' },
  uffici: { label: 'Uffici', color: 'text-sky-400' },
  server: { label: 'Server', color: 'text-purple-400' },
  guest: { label: 'Guest', color: 'text-amber-400' },
  dmz: { label: 'DMZ', color: 'text-red-400' },
  medico: { label: 'Medico', color: 'text-pink-400' },
};

export default function SubnetVlanContent() {
  const [subnets, setSubnets] = useState<Subnet[]>([]);
  const [vlans, setVlans] = useState<Vlan[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'subnet' | 'vlan'>('subnet');
  const [addSubnetOpen, setAddSubnetOpen] = useState(false);
  const [addVlanOpen, setAddVlanOpen] = useState(false);
  const [editSubnetOpen, setEditSubnetOpen] = useState(false);
  const [editVlanOpen, setEditVlanOpen] = useState(false);
  const [editingSubnet, setEditingSubnet] = useState<Subnet | null>(null);
  const [editingVlan, setEditingVlan] = useState<Vlan | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<{ open: boolean; type: string; id: string; name: string }>({ open: false, type: '', id: '', name: '' });
  const [newSubnet, setNewSubnet] = useState({ nome: '', cidr: '', gateway: '', area: 'uffici', descrizione: '' });
  const [newVlan, setNewVlan] = useState({ nome: '', vlan_id: '', area: 'uffici', descrizione: '', subnet_id: '' });
  const [editSubnetForm, setEditSubnetForm] = useState({ nome: '', cidr: '', gateway: '', area: 'uffici', descrizione: '' });
  const [editVlanForm, setEditVlanForm] = useState({ nome: '', vlan_id: '', area: 'uffici', descrizione: '', subnet_id: '' });
  const [saving, setSaving] = useState(false);
  const { user, canAdmin } = useAuth();

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [{ data: sn }, { data: vl }] = await Promise.all([
        supabase.from('subnets').select('*').order('cidr'),
        supabase.from('vlans').select('*, subnet:subnets(nome, cidr)').order('vlan_id'),
      ]);
      setSubnets((sn || []) as Subnet[]);
      setVlans((vl || []) as Vlan[]);
    } catch (err: any) {
      toast.error('Errore: ' + err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const validateCIDR = (cidr: string) => /^(\d{1,3}\.){3}\d{1,3}\/([0-9]|[1-2][0-9]|3[0-2])$/.test(cidr);
  const validateVlanId = (id: string) => { const n = parseInt(id); return !isNaN(n) && n >= 1 && n <= 4094; };

  const handleAddSubnet = async () => {
    if (!newSubnet.nome || !newSubnet.cidr) { toast.error('Nome e CIDR sono obbligatori'); return; }
    if (!validateCIDR(newSubnet.cidr)) { toast.error('Formato CIDR non valido (es. 10.0.0.0/24)'); return; }
    setSaving(true);
    try {
      const ipTotali = newSubnet.cidr.includes('/') ? Math.pow(2, 32 - parseInt(newSubnet.cidr.split('/')[1])) - 2 : 0;
      const { error } = await supabase.from('subnets').insert({
        nome: newSubnet.nome,
        cidr: newSubnet.cidr,
        gateway: newSubnet.gateway || null,
        area: newSubnet.area,
        descrizione: newSubnet.descrizione,
        ip_totali: ipTotali,
        ip_usati: 0,
      });
      if (error) throw error;
      toast.success('Subnet creata con successo');
      setAddSubnetOpen(false);
      setNewSubnet({ nome: '', cidr: '', gateway: '', area: 'uffici', descrizione: '' });
      fetchData();
    } catch (err: any) {
      toast.error('Errore: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleAddVlan = async () => {
    if (!newVlan.nome || !newVlan.vlan_id) { toast.error('Nome e ID VLAN sono obbligatori'); return; }
    if (!validateVlanId(newVlan.vlan_id)) { toast.error('ID VLAN deve essere tra 1 e 4094'); return; }
    setSaving(true);
    try {
      const { error } = await supabase.from('vlans').insert({
        nome: newVlan.nome,
        vlan_id: parseInt(newVlan.vlan_id),
        area: newVlan.area,
        descrizione: newVlan.descrizione,
        subnet_id: newVlan.subnet_id || null,
      });
      if (error) throw error;
      toast.success('VLAN creata con successo');
      setAddVlanOpen(false);
      setNewVlan({ nome: '', vlan_id: '', area: 'uffici', descrizione: '', subnet_id: '' });
      fetchData();
    } catch (err: any) {
      toast.error('Errore: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  const openEditSubnet = (s: Subnet) => {
    setEditingSubnet(s);
    setEditSubnetForm({ nome: s.nome, cidr: s.cidr, gateway: s.gateway || '', area: s.area, descrizione: s.descrizione || '' });
    setEditSubnetOpen(true);
  };

  const openEditVlan = (v: Vlan) => {
    setEditingVlan(v);
    setEditVlanForm({ nome: v.nome, vlan_id: String(v.vlan_id), area: v.area, descrizione: v.descrizione || '', subnet_id: v.subnet_id || '' });
    setEditVlanOpen(true);
  };

  const handleEditSubnet = async () => {
    if (!editingSubnet) return;
    if (!editSubnetForm.nome || !editSubnetForm.cidr) { toast.error('Nome e CIDR sono obbligatori'); return; }
    if (!validateCIDR(editSubnetForm.cidr)) { toast.error('Formato CIDR non valido (es. 10.0.0.0/24)'); return; }
    setSaving(true);
    try {
      const ipTotali = editSubnetForm.cidr.includes('/') ? Math.pow(2, 32 - parseInt(editSubnetForm.cidr.split('/')[1])) - 2 : 0;
      const { error } = await supabase.from('subnets').update({
        nome: editSubnetForm.nome,
        cidr: editSubnetForm.cidr,
        gateway: editSubnetForm.gateway || null,
        area: editSubnetForm.area,
        descrizione: editSubnetForm.descrizione,
        ip_totali: ipTotali,
      }).eq('id', editingSubnet.id);
      if (error) throw error;
      toast.success('Subnet aggiornata');
      setEditSubnetOpen(false);
      fetchData();
    } catch (err: any) {
      toast.error('Errore: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleEditVlan = async () => {
    if (!editingVlan) return;
    if (!editVlanForm.nome || !editVlanForm.vlan_id) { toast.error('Nome e ID VLAN sono obbligatori'); return; }
    if (!validateVlanId(editVlanForm.vlan_id)) { toast.error('ID VLAN deve essere tra 1 e 4094'); return; }
    setSaving(true);
    try {
      const { error } = await supabase.from('vlans').update({
        nome: editVlanForm.nome,
        vlan_id: parseInt(editVlanForm.vlan_id),
        area: editVlanForm.area,
        descrizione: editVlanForm.descrizione,
        subnet_id: editVlanForm.subnet_id || null,
      }).eq('id', editingVlan.id);
      if (error) throw error;
      toast.success('VLAN aggiornata');
      setEditVlanOpen(false);
      fetchData();
    } catch (err: any) {
      toast.error('Errore: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    const { type, id } = deleteConfirm;
    setDeleteConfirm({ open: false, type: '', id: '', name: '' });
    try {
      const { error } = await supabase.from(type === 'subnet' ? 'subnets' : 'vlans').delete().eq('id', id);
      if (error) throw error;
      toast.success(`${type === 'subnet' ? 'Subnet' : 'VLAN'} eliminata`);
      fetchData();
    } catch (err: any) {
      toast.error('Errore: ' + err.message);
    }
  };

  const areaOptions = Object.entries(areaLabels).map(([k, v]) => ({ value: k, label: v.label }));

  return (
    <div className="min-h-full bg-background">
      <Toaster position="bottom-right" theme="dark" richColors />
      <Breadcrumb items={[{ label: 'Subnet e VLAN' }]} />

      <ConfirmDialog
        open={deleteConfirm.open}
        title={`Elimina ${deleteConfirm.type === 'subnet' ? 'Subnet' : 'VLAN'}`}
        message={`Sei sicuro di voler eliminare "${deleteConfirm.name}"? Questa azione non può essere annullata.`}
        confirmLabel="Elimina"
        onConfirm={handleDelete}
        onCancel={() => setDeleteConfirm({ open: false, type: '', id: '', name: '' })}
        danger
      />

      {/* Add Subnet Modal */}
      {addSubnetOpen && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4">
          <div className="bg-surface border border-border rounded-2xl w-full max-w-lg shadow-2xl">
            <div className="flex items-center justify-between px-6 py-5 border-b border-border">
              <h2 className="text-lg font-bold text-white">Aggiungi Subnet</h2>
              <button onClick={() => setAddSubnetOpen(false)} className="p-2 rounded-lg hover:bg-surface-elevated"><Icon name="XMarkIcon" size={18} className="text-muted-color" /></button>
            </div>
            <div className="px-6 py-5 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-secondary-color mb-2">Nome *</label>
                  <input value={newSubnet.nome} onChange={e => setNewSubnet(p => ({ ...p, nome: e.target.value }))} placeholder="es. Rete Laboratorio" className="w-full bg-surface-elevated border border-border rounded-xl px-4 py-3 text-base text-white placeholder:text-muted-color focus:outline-none focus:border-sky-500 transition-colors" />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-secondary-color mb-2">CIDR *</label>
                  <input value={newSubnet.cidr} onChange={e => setNewSubnet(p => ({ ...p, cidr: e.target.value }))} placeholder="es. 10.10.5.0/24" className="w-full bg-surface-elevated border border-border rounded-xl px-4 py-3 text-base font-mono text-white placeholder:text-muted-color focus:outline-none focus:border-sky-500 transition-colors" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-secondary-color mb-2">Gateway</label>
                  <input value={newSubnet.gateway} onChange={e => setNewSubnet(p => ({ ...p, gateway: e.target.value }))} placeholder="es. 10.10.5.1" className="w-full bg-surface-elevated border border-border rounded-xl px-4 py-3 text-base font-mono text-white placeholder:text-muted-color focus:outline-none focus:border-sky-500 transition-colors" />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-secondary-color mb-2">Area</label>
                  <select value={newSubnet.area} onChange={e => setNewSubnet(p => ({ ...p, area: e.target.value }))} className="w-full bg-surface-elevated border border-border rounded-xl px-4 py-3 text-base text-white focus:outline-none focus:border-sky-500 transition-colors">
                    {areaOptions.map(a => <option key={a.value} value={a.value}>{a.label}</option>)}
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-sm font-semibold text-secondary-color mb-2">Descrizione</label>
                <input value={newSubnet.descrizione} onChange={e => setNewSubnet(p => ({ ...p, descrizione: e.target.value }))} placeholder="Descrizione opzionale" className="w-full bg-surface-elevated border border-border rounded-xl px-4 py-3 text-base text-white placeholder:text-muted-color focus:outline-none focus:border-sky-500 transition-colors" />
              </div>
            </div>
            <div className="flex gap-3 px-6 pb-6">
              <button onClick={() => setAddSubnetOpen(false)} className="flex-1 py-3 rounded-xl border border-border text-sm font-semibold text-secondary-color hover:text-white hover:bg-surface-elevated transition-all">Annulla</button>
              <button onClick={handleAddSubnet} disabled={saving} className="flex-1 py-3 rounded-xl bg-sky-500 hover:bg-sky-400 disabled:opacity-60 text-white text-sm font-bold transition-all">{saving ? 'Salvataggio...' : 'Crea Subnet'}</button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Subnet Modal */}
      {editSubnetOpen && editingSubnet && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4">
          <div className="bg-surface border border-border rounded-2xl w-full max-w-lg shadow-2xl">
            <div className="flex items-center justify-between px-6 py-5 border-b border-border">
              <h2 className="text-lg font-bold text-white">Modifica Subnet</h2>
              <button onClick={() => setEditSubnetOpen(false)} className="p-2 rounded-lg hover:bg-surface-elevated"><Icon name="XMarkIcon" size={18} className="text-muted-color" /></button>
            </div>
            <div className="px-6 py-5 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-secondary-color mb-2">Nome *</label>
                  <input value={editSubnetForm.nome} onChange={e => setEditSubnetForm(p => ({ ...p, nome: e.target.value }))} className="w-full bg-surface-elevated border border-border rounded-xl px-4 py-3 text-base text-white focus:outline-none focus:border-sky-500 transition-colors" />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-secondary-color mb-2">CIDR *</label>
                  <input value={editSubnetForm.cidr} onChange={e => setEditSubnetForm(p => ({ ...p, cidr: e.target.value }))} className="w-full bg-surface-elevated border border-border rounded-xl px-4 py-3 text-base font-mono text-white focus:outline-none focus:border-sky-500 transition-colors" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-secondary-color mb-2">Gateway</label>
                  <input value={editSubnetForm.gateway} onChange={e => setEditSubnetForm(p => ({ ...p, gateway: e.target.value }))} className="w-full bg-surface-elevated border border-border rounded-xl px-4 py-3 text-base font-mono text-white focus:outline-none focus:border-sky-500 transition-colors" />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-secondary-color mb-2">Area</label>
                  <select value={editSubnetForm.area} onChange={e => setEditSubnetForm(p => ({ ...p, area: e.target.value }))} className="w-full bg-surface-elevated border border-border rounded-xl px-4 py-3 text-base text-white focus:outline-none focus:border-sky-500 transition-colors">
                    {areaOptions.map(a => <option key={a.value} value={a.value}>{a.label}</option>)}
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-sm font-semibold text-secondary-color mb-2">Descrizione</label>
                <input value={editSubnetForm.descrizione} onChange={e => setEditSubnetForm(p => ({ ...p, descrizione: e.target.value }))} className="w-full bg-surface-elevated border border-border rounded-xl px-4 py-3 text-base text-white focus:outline-none focus:border-sky-500 transition-colors" />
              </div>
            </div>
            <div className="flex gap-3 px-6 pb-6">
              <button onClick={() => setEditSubnetOpen(false)} className="flex-1 py-3 rounded-xl border border-border text-sm font-semibold text-secondary-color hover:text-white hover:bg-surface-elevated transition-all">Annulla</button>
              <button onClick={handleEditSubnet} disabled={saving} className="flex-1 py-3 rounded-xl bg-sky-500 hover:bg-sky-400 disabled:opacity-60 text-white text-sm font-bold transition-all">{saving ? 'Salvataggio...' : 'Salva Modifiche'}</button>
            </div>
          </div>
        </div>
      )}

      {/* Add VLAN Modal */}
      {addVlanOpen && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4">
          <div className="bg-surface border border-border rounded-2xl w-full max-w-lg shadow-2xl">
            <div className="flex items-center justify-between px-6 py-5 border-b border-border">
              <h2 className="text-lg font-bold text-white">Aggiungi VLAN</h2>
              <button onClick={() => setAddVlanOpen(false)} className="p-2 rounded-lg hover:bg-surface-elevated"><Icon name="XMarkIcon" size={18} className="text-muted-color" /></button>
            </div>
            <div className="px-6 py-5 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-secondary-color mb-2">Nome *</label>
                  <input value={newVlan.nome} onChange={e => setNewVlan(p => ({ ...p, nome: e.target.value }))} placeholder="es. VLAN Radiologia" className="w-full bg-surface-elevated border border-border rounded-xl px-4 py-3 text-base text-white placeholder:text-muted-color focus:outline-none focus:border-sky-500 transition-colors" />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-secondary-color mb-2">ID VLAN * (1-4094)</label>
                  <input type="number" min="1" max="4094" value={newVlan.vlan_id} onChange={e => setNewVlan(p => ({ ...p, vlan_id: e.target.value }))} placeholder="es. 50" className="w-full bg-surface-elevated border border-border rounded-xl px-4 py-3 text-base font-mono text-white placeholder:text-muted-color focus:outline-none focus:border-sky-500 transition-colors" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-secondary-color mb-2">Area</label>
                  <select value={newVlan.area} onChange={e => setNewVlan(p => ({ ...p, area: e.target.value }))} className="w-full bg-surface-elevated border border-border rounded-xl px-4 py-3 text-base text-white focus:outline-none focus:border-sky-500 transition-colors">
                    {areaOptions.map(a => <option key={a.value} value={a.value}>{a.label}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-secondary-color mb-2">Subnet Associata</label>
                  <select value={newVlan.subnet_id} onChange={e => setNewVlan(p => ({ ...p, subnet_id: e.target.value }))} className="w-full bg-surface-elevated border border-border rounded-xl px-4 py-3 text-base text-white focus:outline-none focus:border-sky-500 transition-colors">
                    <option value="">Nessuna</option>
                    {subnets.map(s => <option key={s.id} value={s.id}>{s.nome} ({s.cidr})</option>)}
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-sm font-semibold text-secondary-color mb-2">Descrizione</label>
                <input value={newVlan.descrizione} onChange={e => setNewVlan(p => ({ ...p, descrizione: e.target.value }))} placeholder="Descrizione opzionale" className="w-full bg-surface-elevated border border-border rounded-xl px-4 py-3 text-base text-white placeholder:text-muted-color focus:outline-none focus:border-sky-500 transition-colors" />
              </div>
            </div>
            <div className="flex gap-3 px-6 pb-6">
              <button onClick={() => setAddVlanOpen(false)} className="flex-1 py-3 rounded-xl border border-border text-sm font-semibold text-secondary-color hover:text-white hover:bg-surface-elevated transition-all">Annulla</button>
              <button onClick={handleAddVlan} disabled={saving} className="flex-1 py-3 rounded-xl bg-sky-500 hover:bg-sky-400 disabled:opacity-60 text-white text-sm font-bold transition-all">{saving ? 'Salvataggio...' : 'Crea VLAN'}</button>
            </div>
          </div>
        </div>
      )}

      {/* Edit VLAN Modal */}
      {editVlanOpen && editingVlan && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4">
          <div className="bg-surface border border-border rounded-2xl w-full max-w-lg shadow-2xl">
            <div className="flex items-center justify-between px-6 py-5 border-b border-border">
              <h2 className="block text-lg font-bold text-white">Modifica VLAN</h2>
              <button onClick={() => setEditVlanOpen(false)} className="p-2 rounded-lg hover:bg-surface-elevated"><Icon name="XMarkIcon" size={18} className="text-muted-color" /></button>
            </div>
            <div className="px-6 py-5 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-secondary-color mb-2">Nome *</label>
                  <input value={editVlanForm.nome} onChange={e => setEditVlanForm(p => ({ ...p, nome: e.target.value }))} className="w-full bg-surface-elevated border border-border rounded-xl px-4 py-3 text-base text-white focus:outline-none focus:border-sky-500 transition-colors" />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-secondary-color mb-2">ID VLAN * (1-4094)</label>
                  <input type="number" min="1" max="4094" value={editVlanForm.vlan_id} onChange={e => setEditVlanForm(p => ({ ...p, vlan_id: e.target.value }))} className="w-full bg-surface-elevated border border-border rounded-xl px-4 py-3 text-base font-mono text-white focus:outline-none focus:border-sky-500 transition-colors" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-secondary-color mb-2">Area</label>
                  <select value={editVlanForm.area} onChange={e => setEditVlanForm(p => ({ ...p, area: e.target.value }))} className="w-full bg-surface-elevated border border-border rounded-xl px-4 py-3 text-base text-white focus:outline-none focus:border-sky-500 transition-colors">
                    {areaOptions.map(a => <option key={a.value} value={a.value}>{a.label}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-secondary-color mb-2">Subnet Associata</label>
                  <select value={editVlanForm.subnet_id} onChange={e => setEditVlanForm(p => ({ ...p, subnet_id: e.target.value }))} className="w-full bg-surface-elevated border border-border rounded-xl px-4 py-3 text-base text-white focus:outline-none focus:border-sky-500 transition-colors">
                    <option value="">Nessuna</option>
                    {subnets.map(s => <option key={s.id} value={s.id}>{s.nome} ({s.cidr})</option>)}
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-sm font-semibold text-secondary-color mb-2">Descrizione</label>
                <input value={editVlanForm.descrizione} onChange={e => setEditVlanForm(p => ({ ...p, descrizione: e.target.value }))} className="w-full bg-surface-elevated border border-border rounded-xl px-4 py-3 text-base text-white focus:outline-none focus:border-sky-500 transition-colors" />
              </div>
            </div>
            <div className="flex gap-3 px-6 pb-6">
              <button onClick={() => setEditVlanOpen(false)} className="flex-1 py-3 rounded-xl border border-border text-sm font-semibold text-secondary-color hover:text-white hover:bg-surface-elevated transition-all">Annulla</button>
              <button onClick={handleEditVlan} disabled={saving} className="flex-1 py-3 rounded-xl bg-sky-500 hover:bg-sky-400 disabled:opacity-60 text-white text-sm font-bold transition-all">{saving ? 'Salvataggio...' : 'Salva Modifiche'}</button>
            </div>
          </div>
        </div>
      )}

      <div className="max-w-screen-2xl mx-auto px-6 lg:px-8 xl:px-10 py-6 space-y-5">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h1 className="text-3xl font-bold text-white">Sottoreti e VLAN</h1>
            <p className="text-base text-secondary-color mt-1">
              {loading ? 'Caricamento...' : `${subnets.length} subnet · ${vlans.length} VLAN configurate`}
            </p>
          </div>
          {canAdmin() && (
            <div className="flex gap-2">
              <button onClick={() => setAddSubnetOpen(true)} className="flex items-center gap-2 bg-surface border border-border rounded-lg px-4 py-2.5 text-sm font-semibold text-secondary-color hover:text-white hover:bg-surface-elevated transition-all">
                <Icon name="PlusIcon" size={17} />
                Nuova Subnet
              </button>
              <button onClick={() => setAddVlanOpen(true)} className="flex items-center gap-2 bg-sky-500 hover:bg-sky-400 text-white rounded-lg px-5 py-2.5 text-sm font-bold transition-all active:scale-95">
                <Icon name="PlusIcon" size={18} />
                Nuova VLAN
              </button>
            </div>
          )}
        </div>

        {/* Tabs */}
        <div className="flex gap-1 bg-surface border border-border rounded-xl p-1 w-fit">
          {[{ key: 'subnet', label: 'Subnet' }, { key: 'vlan', label: 'VLAN' }].map(t => (
            <button
              key={t.key}
              onClick={() => setActiveTab(t.key as any)}
              className={`px-5 py-2 rounded-lg text-sm font-semibold transition-all ${activeTab === t.key ? 'bg-sky-500 text-white' : 'text-secondary-color hover:text-white'}`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="flex flex-col items-center gap-4 py-16">
            <svg className="animate-spin w-8 h-8 text-sky-400" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
            </svg>
            <p className="text-base text-secondary-color">Caricamento...</p>
          </div>
        ) : activeTab === 'subnet' ? (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {subnets.map(s => {
              const pct = s.ip_totali > 0 ? Math.round((s.ip_usati / s.ip_totali) * 100) : 0;
              const ac = areaLabels[s.area] || { label: s.area, color: 'text-secondary-color' };
              return (
                <div key={s.id} className="bg-surface border border-border rounded-xl p-5 hover:border-sky-500/30 transition-colors">
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <h3 className="text-base font-bold text-white">{s.nome}</h3>
                      <p className={`text-xs font-semibold mt-0.5 ${ac.color}`}>{ac.label}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="font-mono text-sm font-bold text-sky-400 bg-sky-500/10 px-2 py-1 rounded-lg">{s.cidr}</span>
                      {canAdmin() && (
                        <>
                          <button onClick={() => openEditSubnet(s)} className="p-1.5 rounded-lg hover:bg-sky-500/10 text-muted-color hover:text-sky-400 transition-colors">
                            <Icon name="PencilIcon" size={14} />
                          </button>
                          <button onClick={() => setDeleteConfirm({ open: true, type: 'subnet', id: s.id, name: s.nome })} className="p-1.5 rounded-lg hover:bg-red-500/10 text-muted-color hover:text-red-400 transition-colors">
                            <Icon name="TrashIcon" size={14} />
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                  {s.descrizione && <p className="text-xs text-muted-color mb-3">{s.descrizione}</p>}
                  <div className="space-y-2">
                    <div className="flex justify-between text-xs">
                      <span className="text-muted-color">Utilizzo IP</span>
                      <span className={`font-semibold ${pct > 85 ? 'text-red-400' : pct > 60 ? 'text-amber-400' : 'text-green-400'}`}>{pct}%</span>
                    </div>
                    <div className="h-2 bg-surface-elevated rounded-full overflow-hidden">
                      <div className={`h-full rounded-full transition-all ${pct > 85 ? 'bg-red-500' : pct > 60 ? 'bg-amber-500' : 'bg-green-500'}`} style={{ width: `${pct}%` }} />
                    </div>
                    <div className="flex justify-between text-xs text-muted-color">
                      <span>{s.ip_usati} usati</span>
                      <span>{s.ip_totali - s.ip_usati} disponibili</span>
                    </div>
                  </div>
                  {s.gateway && (
                    <div className="mt-3 pt-3 border-t border-border flex justify-between text-xs">
                      <span className="text-muted-color">Gateway</span>
                      <span className="font-mono text-white">{s.gateway}</span>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        ) : (
          <div className="bg-surface border border-border rounded-xl overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border bg-surface-elevated/50">
                  <th className="px-5 py-3 text-left text-xs font-medium text-muted-color uppercase tracking-wide">ID VLAN</th>
                  <th className="px-5 py-3 text-left text-xs font-medium text-muted-color uppercase tracking-wide">Nome</th>
                  <th className="px-5 py-3 text-left text-xs font-medium text-muted-color uppercase tracking-wide">Area</th>
                  <th className="px-5 py-3 text-left text-xs font-medium text-muted-color uppercase tracking-wide">Subnet</th>
                  <th className="px-5 py-3 text-left text-xs font-medium text-muted-color uppercase tracking-wide">Descrizione</th>
                  {canAdmin() && <th className="px-5 py-3 text-left text-xs font-medium text-muted-color uppercase tracking-wide">Azioni</th>}
                </tr>
              </thead>
              <tbody>
                {vlans.length === 0 ? (
                  <tr><td colSpan={6} className="px-5 py-12 text-center text-secondary-color">Nessuna VLAN configurata</td></tr>
                ) : vlans.map((v, idx) => {
                  const ac = areaLabels[v.area] || { label: v.area, color: 'text-secondary-color' };
                  return (
                    <tr key={v.id} className={`border-b border-border/50 ${idx % 2 !== 0 ? 'bg-surface-elevated/20' : ''} hover:bg-surface-elevated/40 transition-colors`}>
                      <td className="px-5 py-3.5">
                        <span className="font-mono font-bold text-sky-400 bg-sky-500/10 px-2 py-0.5 rounded-lg text-xs">VLAN {v.vlan_id}</span>
                      </td>
                      <td className="px-5 py-3.5 font-semibold text-white">{v.nome}</td>
                      <td className="px-5 py-3.5"><span className={`text-sm font-semibold ${ac.color}`}>{ac.label}</span></td>
                      <td className="px-5 py-3.5 font-mono text-secondary-color text-xs">{(v.subnet as any)?.cidr || '—'}</td>
                      <td className="px-5 py-3.5 text-secondary-color text-xs">{v.descrizione || '—'}</td>
                      {canAdmin() && (
                        <td className="px-5 py-3.5">
                          <div className="flex gap-1">
                            <button onClick={() => openEditVlan(v)} className="p-1.5 rounded-lg hover:bg-sky-500/10 text-muted-color hover:text-sky-400 transition-colors">
                              <Icon name="PencilIcon" size={14} />
                            </button>
                            <button onClick={() => setDeleteConfirm({ open: true, type: 'vlan', id: v.id, name: v.nome })} className="p-1.5 rounded-lg hover:bg-red-500/10 text-muted-color hover:text-red-400 transition-colors">
                              <Icon name="TrashIcon" size={14} />
                            </button>
                          </div>
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
